import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { Tag, Plus, RefreshCw, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminCategories = () => {
    const { t } = useTranslation();
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newCategory, setNewCategory] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) navigate('/admin');
    }, [authLoading, isSuperAdmin, navigate]);

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}tickets.php?action=categories`);
            const data = await response.json();
            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error(t('adminCategories.toastLoadError') || 'კატეგორიების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user) fetchCategories();
    }, [user, fetchCategories]);

    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.trim()) return;
        setIsCreating(true);
        try {
            const sanitized = sanitizePayload({ name: newCategory });
            const response = await fetchWithSecurity(`${API_URL}tickets.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'create_category', name: sanitized.name })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('adminCategories.toastCreated') || 'კატეგორია წარმატებით დაემატა');
                setNewCategory('');
                fetchCategories();
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error(t('adminCategories.toastCreateError') || 'კატეგორიის დამატება ვერ მოხერხდა');
        }
        setIsCreating(false);
    };

    const handleNameChange = async (catId, newName) => {
        if (!newName.trim()) return;
        try {
            const sanitized = sanitizePayload({ name: newName });
            const response = await fetchWithSecurity(`${API_URL}tickets.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_category', id: catId, name: sanitized.name })
            });
            const result = await response.json();
            if (result.success) {
                toast.success(t('adminCategories.toastUpdated') || 'კატეგორია განახლდა');
            } else {
                toast.error(result.error);
            }
        } catch (error) {
            toast.error(t('adminCategories.toastUpdateError') || 'კატეგორიის განახლება ვერ მოხერხდა');
        }
    };

    const deleteCategory = async (catId) => {
        if (window.confirm(t('adminCategories.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ კატეგორიის წაშლა?')) {
            try {
                const response = await fetchWithSecurity(`${API_URL}tickets.php?category_id=${catId}`, { method: 'DELETE' });
                const result = await response.json();
                if (result.success) {
                    toast.success(t('adminCategories.toastDeleted') || 'კატეგორია წაიშალა');
                    fetchCategories();
                } else {
                    toast.error(result.error);
                }
            } catch (error) {
                toast.error(t('adminCategories.toastDeleteError') || 'კატეგორიის წაშლა ვერ მოხერხდა');
            }
        }
    };

    if (authLoading || !user) return null;

    return (
        <AdminLayout title={t('adminCategories.title') || 'კატეგორიების მართვა'}>
            <div className="row g-4">
                {/* Create Category Form */}
                <div className="col-lg-4">
                    <div className="shadcn-card h-100">
                        <div className="shadcn-card-header">
                            <div className="shadcn-card-title">
                                <Plus size={18} className="text-indigo" />
                                <span>{t('adminCategories.newCategory') || 'ახალი კატეგორიის დამატება'}</span>
                            </div>
                        </div>

                        <form onSubmit={handleCreateCategory}>
                            <div className="mb-4">
                                <label className="form-group-label">{t('adminCategories.name') || 'კატეგორიის დასახელება'}</label>
                                <input 
                                    type="text" 
                                    className="shadcn-input" 
                                    value={newCategory}
                                    onChange={(e) => setNewCategory(e.target.value)}
                                    required 
                                    placeholder={t('adminCategories.namePlaceholder') || 'შეიყვანეთ კატეგორიის დასახელება...'}
                                />
                            </div>
                            <button type="submit" className="shadcn-btn shadcn-btn-primary w-100" disabled={isCreating}>
                                <Plus size={16} />
                                <span>{isCreating ? (t('adminCategories.creating') || 'ემატება...') : (t('adminCategories.addBtn') || 'კატეგორიის დამატება')}</span>
                            </button>
                        </form>
                    </div>
                </div>

                {/* Categories List */}
                <div className="col-lg-8">
                    <div className="shadcn-card h-100">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title">
                                <Tag size={18} className="text-indigo" />
                                <span>{t('adminCategories.activeCategories') || 'აქტიური კატეგორიები'} ({categories.length})</span>
                            </div>
                            <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchCategories}>
                                <RefreshCw size={14} /> {t('adminCategories.refresh') || 'განახლება'}
                            </button>
                        </div>

                        {loading ? (
                            <div className="text-center py-5 text-muted">
                                <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                                <span>{t('adminCategories.loading') || 'კატეგორიები იტვირთება...'}</span>
                            </div>
                        ) : (
                            <div className="shadcn-table-wrapper">
                                <table className="shadcn-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>{t('adminCategories.colName') || 'კატეგორიის დასახელება'}</th>
                                            <th>{t('adminCategories.colDate') || 'შექმნის თარიღი'}</th>
                                            <th className="text-end">{t('adminCategories.colActions') || 'მოქმედებები'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categories.map(cat => (
                                            <tr key={cat.id}>
                                                <td className="text-muted">#{cat.id}</td>
                                                <td>
                                                    <input 
                                                        type="text" 
                                                        className="shadcn-input py-1 border-0 bg-transparent fw-bold text-dark" 
                                                        defaultValue={cat.name}
                                                        onBlur={(e) => {
                                                            if (e.target.value !== cat.name) {
                                                                handleNameChange(cat.id, e.target.value);
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td className="small text-muted">{new Date(cat.created_at).toLocaleDateString()}</td>
                                                <td className="text-end">
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteCategory(cat.id)} title={t('common.delete') || 'წაშლა'}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminCategories;
